// Step 1: the address and token, checked, then a scan: every project a
// YouTrack token can read, or every shelf a BookStack token can see.
import { type FormEvent, useState } from "react"
import { Button, ErrorNote, Field } from "../../ui"
import { type MigrationCheck, api } from "../../../lib/api"
import type { Batch, BatchSource } from "../../../lib/batchMigrations"
import { formatCount } from "../../../lib/migrations"
import { Note, StepLayout } from "../parts"

const BASE = "/platform/migrations/batches"

export function ConnectStep({ source, onCreated }: { source: BatchSource; onCreated: (batch: Batch) => void }) {
  if (source === "bookstack") return <BookstackConnect onCreated={onCreated} />
  return <YouTrackConnect onCreated={onCreated} />
}

function BookstackConnect({ onCreated }: { onCreated: (batch: Batch) => void }) {
  const [fields, setFields] = useState({ url: "", tokenId: "", tokenSecret: "" })
  const [check, setCheck] = useState<{ shelves: number; books: number } | null>(null)
  const [busy, setBusy] = useState<"check" | "scan" | null>(null)
  const [error, setError] = useState<unknown>(null)
  const complete = fields.url.trim() && fields.tokenId.trim() && fields.tokenSecret.trim()
  const edit = (change: Partial<typeof fields>) => {
    setFields({ ...fields, ...change })
    setCheck(null)
  }

  const runCheck = async (e: FormEvent) => {
    e.preventDefault()
    setBusy("check")
    setError(null)
    try {
      setCheck(await api.post<{ shelves: number; books: number }>(`${BASE}/check`, { source: "bookstack", fields }))
    } catch (err) {
      setError(err)
    } finally {
      setBusy(null)
    }
  }

  const scan = async () => {
    setBusy("scan")
    setError(null)
    try {
      const { batch } = await api.post<{ batch: Batch }>(BASE, { source: "bookstack", fields })
      onCreated(batch)
    } catch (err) {
      setError(err)
      setBusy(null)
    }
  }

  return (
    <StepLayout
      title="Connect to BookStack"
      intro="A batch reads every shelf the token can see and proposes a customer company for each. Each shelf you tick becomes one Context Hub space holding all of its books."
      guide={{
        points: [
          "Checks the address and token work.",
          "Scans every shelf with its books, and generates the mapping: a shelf named like a company goes to it.",
          "Nothing is created until you start the import.",
        ],
        tip: 'Create the token under your BookStack profile → API Tokens. Its role needs "Access system API", and the user must be able to see the shelves. Each new space keeps the token, so it can sync again later.',
      }}
      footer={
        <Button type="button" className="ml-auto" disabled={!check || busy !== null} onClick={scan}>
          {busy === "scan" ? "Starting…" : "Scan shelves"}
        </Button>
      }
    >
      <form className="flex flex-col gap-5" onSubmit={runCheck}>
        <Field
          id="batch-url"
          label="BookStack address"
          placeholder="https://wiki.acme.com"
          hint="The address you open BookStack with, without /api."
          autoComplete="off"
          value={fields.url}
          onChange={(e) => edit({ url: e.target.value })}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            id="batch-token-id"
            label="Token ID"
            autoComplete="off"
            value={fields.tokenId}
            onChange={(e) => edit({ tokenId: e.target.value })}
          />
          <Field
            id="batch-token-secret"
            label="Token secret"
            type="password"
            hint="Encrypted while the batch runs."
            autoComplete="off"
            value={fields.tokenSecret}
            onChange={(e) => edit({ tokenSecret: e.target.value })}
          />
        </div>
        <div>
          <Button type="submit" variant="ghost" disabled={busy !== null || !complete}>
            {busy === "check" ? "Checking…" : check ? "Check again" : "Check connection"}
          </Button>
        </div>
        <ErrorNote error={error} />
        {check && (
          <Note tone="success">
            Connected. This token sees {formatCount(check.shelves)} {check.shelves === 1 ? "shelf" : "shelves"} and{" "}
            {formatCount(check.books)} book{check.books === 1 ? "" : "s"}.
          </Note>
        )}
      </form>
    </StepLayout>
  )
}

function YouTrackConnect({ onCreated }: { onCreated: (batch: Batch) => void }) {
  const [fields, setFields] = useState({ url: "", token: "" })
  const [check, setCheck] = useState<MigrationCheck | null>(null)
  const [busy, setBusy] = useState<"check" | "scan" | null>(null)
  const [error, setError] = useState<unknown>(null)
  const complete = fields.url.trim() && fields.token.trim()

  const runCheck = async (e: FormEvent) => {
    e.preventDefault()
    setBusy("check")
    setError(null)
    try {
      setCheck(await api.post<MigrationCheck>(`${BASE}/check`, { source: "youtrack", fields }))
    } catch (err) {
      setError(err)
    } finally {
      setBusy(null)
    }
  }

  const scan = async () => {
    setBusy("scan")
    setError(null)
    try {
      const { batch } = await api.post<{ batch: Batch }>(BASE, { source: "youtrack", fields })
      onCreated(batch)
    } catch (err) {
      setError(err)
      setBusy(null)
    }
  }

  return (
    <StepLayout
      title="Connect to YouTrack"
      intro="A batch reads every project the token can see, then imports the ones you choose, each into its customer's company. It only reads YouTrack, and the token is deleted when the batch ends."
      guide={{
        points: [
          "Checks the address and token work.",
          "Scans every project with its issue count and agile boards.",
          "Nothing is created until you start the import.",
        ],
        tip: "Use a token of a YouTrack admin: it needs Read Issue and Read User in every project, and access to the agile boards for sprints.",
      }}
      footer={
        <Button type="button" className="ml-auto" disabled={!check || busy !== null} onClick={scan}>
          {busy === "scan" ? "Starting…" : "Scan projects"}
        </Button>
      }
    >
      <form className="flex flex-col gap-5" onSubmit={runCheck}>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field
            id="batch-url"
            label="YouTrack address"
            placeholder="https://acme.youtrack.cloud"
            hint="The address you open YouTrack with, without a project path."
            autoComplete="off"
            value={fields.url}
            onChange={(e) => {
              setFields({ ...fields, url: e.target.value })
              setCheck(null)
            }}
          />
          <Field
            id="batch-token"
            label="Permanent token"
            type="password"
            placeholder="perm:…"
            hint="Encrypted while the batch runs."
            autoComplete="off"
            value={fields.token}
            onChange={(e) => {
              setFields({ ...fields, token: e.target.value })
              setCheck(null)
            }}
          />
        </div>
        <div>
          <Button type="submit" variant="ghost" disabled={busy !== null || !complete}>
            {busy === "check" ? "Checking…" : check ? "Check again" : "Check connection"}
          </Button>
        </div>
        <ErrorNote error={error} />
        {check && (
          <div className="flex flex-col gap-2">
            <Note tone="success">
              Connected as <b className="font-semibold">{check.account}</b>. This token can read{" "}
              {formatCount(check.projects.length)} project{check.projects.length === 1 ? "" : "s"}.
            </Note>
            {!check.canReadEmails && (
              <Note tone="warning">
                People's email addresses are hidden from this token, so nobody can be linked to an existing account.
              </Note>
            )}
            {!check.canReadBoards && <Note tone="warning">This token can't read agile boards, so no sprints will come across.</Note>}
            {check.warnings.map((w) => (
              <Note key={w} tone="warning">
                {w}
              </Note>
            ))}
          </div>
        )}
      </form>
    </StepLayout>
  )
}
